import styles from './input.module.css';
import { memo } from 'react';
import { HidePasswordIcon, ShowPasswordIcon } from '../../assets/icons';
import { DescriptionBlock } from '../DescriptionBlock/DescriptionBlock';

export interface IOptions {
	optionText: string,
    optionType: string,
}

interface IInputProps<T> {
    value: string;
    onChange: (key: keyof T, value: string) => void;
    type: React.HTMLInputTypeAttribute;
    name: keyof T;
    isRequired: boolean;
	labelText?: string;
	withErrorBlock?: boolean,
	error?: string;
    placeholder?: string;
    className?: string;
	wrapperClassName?: string;
    withPasswordIcon?: boolean;
	options?: IOptions[]; 
    changeTypeFn?: (type: React.HTMLInputTypeAttribute, key: keyof T) => void;
}

const InputComponent = <T,>({
	value,
	onChange,
	type,
	name,
	isRequired,
	labelText,
	withErrorBlock = false,
	error,
	placeholder,
	className,
	wrapperClassName,
	withPasswordIcon = false,
	options,
	changeTypeFn,
}: IInputProps<T>) => {
  	const isPasswordInput = type === 'password';
	const isInvalid = Boolean(error);
	const inputId = String(name);
	
  	return (
		<div className={`${styles.inputBlock} ${wrapperClassName}`}>

			{labelText 
				&& 	<label
						className={styles.inputLabel}
						htmlFor={inputId}
					>
						{labelText}
					</label>
			}

			<div className={styles.inputWrapper}>
				<input
					className={`
						${styles.input}
						${className ?? ''}
						${withPasswordIcon ? styles.withIcon : ''}
						${error ? styles.error : ''}
					`}
					value={value}
					onChange={(e) => onChange(name, e.target.value)}
					type={type}
					id={inputId}
					name={inputId}
					required={isRequired}
					placeholder={placeholder}
				/>

				{withPasswordIcon && (
					<div
						role="button"
  						aria-label="toggle password visibility"
						className={styles.changeTypeIcon}
						onClick={() => changeTypeFn && changeTypeFn(type, name)}
					>
						{isPasswordInput ? <HidePasswordIcon /> : <ShowPasswordIcon />}
					</div>
				)}
			</div>
			

			{options && options?.length !== 0
				&&  <DescriptionBlock>
						<ul className={styles.optionsList}>
							{options.map((option, index) => (
								<li 
									key={index}
									className={styles.option}
								>
								<div className={styles.marker}></div>
									{option.optionText}
								</li>
							))}
						</ul>
					</DescriptionBlock>
        	}

			{withErrorBlock
				&& 	<div
						className={styles.errorText}
						style={{ opacity: !isInvalid ? 0 : 1 }}
					>
						{error}
					</div>
			}

		</div>
  	);
};

export const Input = memo(InputComponent) as typeof InputComponent;
